"use client";

import { useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

interface CodeBlockProps {
  children: string;
  language?: string;
}

export default function CodeBlock({ children, language = "text" }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const code = typeof children === "string" ? children.replace(/\n$/, "") : "";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div
      style={{
        margin: "1.5rem 0",
        borderRadius: "0.5rem",
        border: "1px solid #333",
        overflow: "hidden",
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          backgroundColor: "#252526",
          borderBottom: "1px solid #333",
          padding: "0.4rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ color: "#858585", fontSize: "0.72rem", letterSpacing: "0.05em" }}>
          {language}
        </span>
        <button
          onClick={copyToClipboard}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            padding: "0.25rem 0.6rem",
            borderRadius: "0.25rem",
            border: "none",
            cursor: "pointer",
            fontSize: "0.72rem",
            backgroundColor: copied ? "transparent" : "#3a3a3a",
            color: copied ? "#86efac" : "#aaa",
            transition: "all 0.15s",
          }}
        >
          {copied ? (
            <>
              <svg width="13" height="13" fill="none" stroke="#86efac" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* Syntax-highlighted code */}
      {["text", "bash", "shell"].includes(language) ? (
        <pre
          style={{
            margin: 0,
            padding: "1.25rem 1rem",
            backgroundColor: "#1e1e1e",
            color: "#d4d4d4",
            fontSize: "0.9rem",
            lineHeight: "1.65",
            borderRadius: 0,
            overflowX: "auto",
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
          }}
        >
          <code style={{ fontFamily: "inherit", fontSize: "inherit", color: "#d4d4d4", backgroundColor: "transparent", padding: 0, borderRadius: 0 }}>{code}</code>
        </pre>
      ) : (
        <SyntaxHighlighter
          language={language}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: "1.25rem 1rem",
            backgroundColor: "#1e1e1e",
            fontSize: "0.9rem",
            lineHeight: "1.65",
            borderRadius: 0,
            overflowX: "auto",
          }}
          codeTagProps={{
            style: {
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
              fontSize: "inherit",
              backgroundColor: "transparent",
              color: "#d4d4d4",
            },
          }}
        >
          {code}
        </SyntaxHighlighter>
      )}
    </div>
  );
}
